import React from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Avatar,
  IconButton,
  CircularProgress,
  styled,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  ExitToApp as ExitToAppIcon,
  MoreVert as MoreVertIcon,
  Computer as ComputerIcon,
  Smartphone as SmartphoneIcon,
  Tablet as TabletIcon,
} from "@mui/icons-material";
import { blue, green, red, amber, grey } from "@mui/material/colors";
import { alpha } from "@mui/material/styles";
import FormatDateDurationForMessagesDate from "../../utils/FormatDateDurationForMessagesDate";
import capitalizeEachFirstWord from "../../utils/CapLetters";

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const SessionList = ({
  sessions = [],
  loading = false,
  pagination = {},
  onViewSession,
  onTerminateSession,
  onPreviousPage,
  onNextPage,
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedSession, setSelectedSession] = React.useState(null);

  const handleMenuOpen = (event, session) => {
    setAnchorEl(event.currentTarget);
    setSelectedSession(session);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSession(null);
  };

  const handleViewSession = () => {
    if (selectedSession && onViewSession) {
      onViewSession(selectedSession);
    }
    handleMenuClose();
  };

  const handleTerminateSession = () => {
    if (selectedSession && onTerminateSession) {
      onTerminateSession(selectedSession.sessionId);
    }
    handleMenuClose();
  };

  const getUserTypeIcon = (userType) => {
    return userType === "admin" ? (
      <AdminPanelSettingsIcon fontSize="small" />
    ) : (
      <PersonIcon fontSize="small" />
    );
  };

  const getUserTypeColor = (userType) => {
    return userType === "admin" ? "secondary" : "primary";
  };

  const getDeviceIcon = (userAgent) => {
    if (!userAgent) return <ComputerIcon fontSize="small" />;

    const ua = userAgent.toLowerCase();
    if (
      ua.includes("mobile") ||
      ua.includes("android") ||
      ua.includes("iphone")
    ) {
      return <SmartphoneIcon fontSize="small" />;
    }
    if (ua.includes("tablet") || ua.includes("ipad")) {
      return <TabletIcon fontSize="small" />;
    }
    return <ComputerIcon fontSize="small" />;
  };

  const parseUserAgent = (userAgent) => {
    if (!userAgent) return "Unknown Device";

    // Extract browser and OS info
    let browser = "Unknown";
    let os = "Unknown";

    if (userAgent.includes("Chrome")) browser = "Chrome";
    else if (userAgent.includes("Firefox")) browser = "Firefox";
    else if (userAgent.includes("Safari")) browser = "Safari";
    else if (userAgent.includes("Edge")) browser = "Edge";

    if (userAgent.includes("Windows")) os = "Windows";
    else if (userAgent.includes("Mac")) os = "macOS";
    else if (userAgent.includes("Linux")) os = "Linux";
    else if (userAgent.includes("Android")) os = "Android";
    else if (userAgent.includes("iOS")) os = "iOS";

    return `${browser} / ${os}`;
  };

  const formatTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return FormatDateDurationForMessagesDate(dateString, true);
    } catch {
      return "Invalid date";
    }
  };

  if (loading && sessions.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!loading && sessions.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: "center" }} elevation={3}>
        <Typography variant="h6" color="text.secondary">
          No active sessions found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          No users are currently logged in
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <TableContainer component={Paper} elevation={3}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: grey[100] }}>
              <TableCell>User</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Device</TableCell>
              <TableCell>IP Address</TableCell>
              <TableCell>Last Activity</TableCell>
              <TableCell>Session Started</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sessions.map((session) => (
              <StyledTableRow key={session.sessionId}>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar
                      sx={{
                        bgcolor:
                          session.userType === "admin" ? blue[500] : green[500],
                        mr: 2,
                        width: 32,
                        height: 32,
                      }}
                    >
                      {getUserTypeIcon(session.userType)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                        {session.firstName && session.lastName
                          ? `${capitalizeEachFirstWord(
                              session.firstName
                            )} ${capitalizeEachFirstWord(session.lastName)}`
                          : capitalizeEachFirstWord(session.username) ||
                            "Unknown User"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {session.email}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={getUserTypeIcon(session.userType)}
                    label={session.userType === "admin" ? "Admin" : "User"}
                    size="small"
                    color={getUserTypeColor(session.userType)}
                    variant="outlined"
                  />
                  {session.role && session.userType === "admin" && (
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                    >
                      {session.role.replace("_", " ").toUpperCase()}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {getDeviceIcon(session.userAgent)}
                    <Box>
                      <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                        {parseUserAgent(session.userAgent)}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {session.ipAddress}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatTime(session.lastActivity)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatTime(session.createdAt)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => onViewSession && onViewSession(session)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, session)}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            minWidth: 200,
            "& .MuiMenuItem-root": {
              px: 2,
              py: 1.5,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={handleViewSession}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleTerminateSession} sx={{ color: red[600] }}>
          <ListItemIcon>
            <ExitToAppIcon fontSize="small" sx={{ color: red[600] }} />
          </ListItemIcon>
          <ListItemText>Terminate Session</ListItemText>
        </MenuItem>
      </Menu>

      {/* Pagination */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {sessions.length} of {pagination.total || 0} sessions
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            disabled={!pagination.hasPrev || loading}
            variant="outlined"
            size="small"
            onClick={onPreviousPage}
          >
            Previous
          </Button>
          {pagination.pageNo && pagination.totalPages && (
            <Typography variant="body2" sx={{ px: 2, py: 1 }}>
              Page {pagination.pageNo} of {pagination.totalPages}
            </Typography>
          )}
          <Button
            disabled={!pagination.hasNext || loading}
            variant="outlined"
            size="small"
            onClick={onNextPage}
          >
            Next
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default SessionList;
